import React, { useEffect, useRef, useState } from 'react';
import {
  AgentDetail,
  AgentConfigUpdatePayload,
  emailAgentService,
} from '../../services/emailAgentService';
import { RestartAgentConfirmDialog } from './RestartAgentConfirmDialog';

interface Props {
  agentType: string;
  onClose: () => void;
  onSaved: () => void;
  /** When user chooses Restart after a save that requires it — stop/start without blocking this modal (parent shows card pending state). */
  onRestartAgentInBackground?: (agentType: string) => void;
}

const AGENT_LABELS: Record<string, string> = {
  ddq: 'DDQ Email Agent',
  feedback: 'Feedback Agent',
  ingest: 'Ingest Agent',
};

const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
const DOMAIN_RE = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

function isValidEmail(v: string) {
  return EMAIL_RE.test(v.trim());
}

function isValidAllowlistEntry(v: string) {
  const entry = v.trim().toLowerCase().replace(/^@+/, '');
  return EMAIL_RE.test(entry) || DOMAIN_RE.test(entry);
}

function isValidHttpUrl(v: string) {
  const s = v.trim();
  if (!s) return false;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

type Tab = 'configuration' | 'status';

export function AgentConfigModal({
  agentType,
  onClose,
  onSaved,
  onRestartAgentInBackground,
}: Props) {
  const [detail, setDetail]     = useState<AgentDetail | null>(null);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('configuration');

  // Config fields
  const [enabled, setEnabled]             = useState(true);
  const [userId, setUserId]               = useState('');
  const [senderEmail, setSenderEmail]     = useState('');
  const [allowedEmails, setAllowedEmails] = useState<string[]>([]);
  const [notificationUrl, setNotificationUrl] = useState('');
  const [emailInput, setEmailInput]       = useState('');
  const [emailInputError, setEmailInputError] = useState<string | null>(null);

  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const emailInputRef = useRef<HTMLInputElement>(null);
  /** Values at open — used to detect restart-required edits (mailbox id, allowlist, notification URL). */
  const configSnapshotRef = useRef<{
    msUserId: string;
    allowedKey: string;
    notificationUrl: string;
  } | null>(null);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const pendingRestartRef = useRef<{
    newMs: string;
    newAllowedKey: string;
    newNotificationUrl: string;
    payload: AgentConfigUpdatePayload;
  } | null>(null);

  useEffect(() => {
    setLoading(true);
    emailAgentService.getAgent(agentType).then((d) => {
      setDetail(d);
      setEnabled(d.is_enabled);
      const cfg = d.config || {};
      const allowed = (cfg.allowed_emails || []) as string[];
      setUserId(cfg.ms_user_id || '');
      setSenderEmail(cfg.sender_email || '');
      setAllowedEmails(allowed);
      setNotificationUrl(cfg.notification_url || '');
      configSnapshotRef.current = {
        msUserId: (cfg.ms_user_id || '').trim(),
        allowedKey: normalizeAllowedListKey(allowed),
        notificationUrl: (cfg.notification_url || '').trim(),
      };
      setLoading(false);
    });
  }, [agentType]);

  function normalizeAllowedListKey(emails: string[]): string {
    return [...emails]
      .map((e) => e.toLowerCase().trim())
      .filter(Boolean)
      .sort()
      .join('|');
  }

  // ── Allowed emails helpers ──────────────────────────────────────────────

  const addEmail = () => {
    const trimmed = emailInput.trim().toLowerCase().replace(/^@+/, '');
    if (!trimmed) return;
    if (!isValidAllowlistEntry(trimmed)) {
      setEmailInputError('Enter a valid email or domain (e.g. adalabsai.com).');
      return;
    }
    if (allowedEmails.includes(trimmed)) {
      setEmailInputError('This email is already in the list.');
      return;
    }
    setAllowedEmails((prev) => [...prev, trimmed]);
    setEmailInput('');
    setEmailInputError(null);
  };

  const removeEmail = (email: string) =>
    setAllowedEmails((prev) => prev.filter((e) => e !== email));

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); addEmail(); }
  };

  // ── Validation ─────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (senderEmail.trim() && !isValidEmail(senderEmail))
      errors.senderEmail = 'Please enter a valid email address.';
    if (notificationUrl.trim() && !isValidHttpUrl(notificationUrl))
      errors.notificationUrl = 'Please enter a valid http(s) URL.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validate()) return;
    if (restartDialogOpen) return;
    setError(null);
    // Always send ms_user_id / sender_email (may be "") so the API clears DB overrides
    // when the user empties a field — omitting keys would leave stale JSONB values.
    const payload: AgentConfigUpdatePayload = {
      is_enabled: enabled,
      ms_user_id: userId.trim(),
      sender_email: senderEmail.trim(),
      allowed_emails: allowedEmails,
      notification_url: notificationUrl.trim(),
    };

    const prev = configSnapshotRef.current;
    const newMs = userId.trim();
    const newAllowedKey = normalizeAllowedListKey(allowedEmails);
    const newNotificationUrl = notificationUrl.trim();
    const restartRequiredFieldsChanged =
      !!prev &&
      (prev.msUserId !== newMs ||
        prev.allowedKey !== newAllowedKey ||
        prev.notificationUrl !== newNotificationUrl);

    // Defer API save until user picks Restart or Don't restart (nothing persisted on Cancel).
    if (restartRequiredFieldsChanged && detail?.runtime?.running) {
      pendingRestartRef.current = {
        newMs,
        newAllowedKey,
        newNotificationUrl,
        payload,
      };
      setRestartDialogOpen(true);
      return;
    }

    setSaving(true);
    try {
      await emailAgentService.updateConfig(agentType, payload);
      configSnapshotRef.current = {
        msUserId: newMs,
        allowedKey: newAllowedKey,
        notificationUrl: newNotificationUrl,
      };
      onSaved();
    } catch (e: any) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestartDialogCancel = () => {
    if (saving) return;
    pendingRestartRef.current = null;
    setRestartDialogOpen(false);
    setError(null);
  };

  const handleRestartDialogDontRestart = async () => {
    const p = pendingRestartRef.current;
    if (!p) return;
    setSaving(true);
    setError(null);
    try {
      await emailAgentService.updateConfig(agentType, p.payload);
      configSnapshotRef.current = {
        msUserId: p.newMs,
        allowedKey: p.newAllowedKey,
        notificationUrl: p.newNotificationUrl,
      };
      pendingRestartRef.current = null;
      setRestartDialogOpen(false);
      onSaved();
    } catch (e: any) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestartDialogRestart = async () => {
    const p = pendingRestartRef.current;
    if (!p) return;
    setSaving(true);
    setError(null);
    try {
      await emailAgentService.updateConfig(agentType, p.payload);
      configSnapshotRef.current = {
        msUserId: p.newMs,
        allowedKey: p.newAllowedKey,
        notificationUrl: p.newNotificationUrl,
      };
      pendingRestartRef.current = null;
      setRestartDialogOpen(false);
      if (onRestartAgentInBackground) {
        onRestartAgentInBackground(agentType);
      } else {
        void (async () => {
          try {
            await emailAgentService.stopAgent(agentType);
            await emailAgentService.startAgent(agentType);
          } catch (e) {
            console.error('Restart failed', e);
          }
        })();
      }
      onSaved();
    } catch (e: any) {
      const msg = e?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestClose = () => {
    if (restartDialogOpen) {
      handleRestartDialogCancel();
      return;
    }
    onClose();
  };

  // ── Tab bar component ──────────────────────────────────────────────────

  const tabs: { id: Tab; label: string }[] = [
    { id: 'configuration', label: 'Configuration' },
    { id: 'status',        label: 'Status & Logs'  },
  ];

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 pt-5 pb-0 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-800">
            Configure {AGENT_LABELS[agentType] || agentType}
          </h2>
          <button
            onClick={handleRequestClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex gap-0 px-6 mt-4 border-b flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : activeTab === 'configuration' ? (
            /* ════════════════ CONFIGURATION TAB ════════════════ */
            <div className="space-y-5">

              {/* Enable toggle */}
              <div className="flex items-center justify-between py-1">
                <div>
                  <p className="text-sm font-medium text-gray-700">Enabled</p>
                  <p className="text-xs text-gray-400 mt-0.5">Allow this agent to process incoming emails</p>
                </div>
                <button
                  onClick={() => setEnabled(!enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                    enabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={enabled}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <hr className="border-gray-100" />

              <p className="text-xs text-gray-400">
                Configure agent behavior here. To start an agent, set both Email Agent User ID and Sender
                Email. Allowed Email Addresses are optional.
              </p>

              {/* Email Agent User ID */}
              <div>
                <label
                  htmlFor={`userId-${agentType}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Agent User ID
                  <span className="ml-1 text-xs font-normal text-gray-400">(MS Graph mailbox ID)</span>
                </label>
                <input
                  id={`userId-${agentType}`}
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="e.g. f691527a-9c7f-4596-b1b6-3900da2cca69"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-300"
                />
              </div>

              {/* Sender Email */}
              <div>
                <label
                  htmlFor={`senderEmail-${agentType}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Sender Email
                  <span className="ml-1 text-xs font-normal text-gray-400">(unique across all agents)</span>
                </label>
                <input
                  id={`senderEmail-${agentType}`}
                  type="email"
                  value={senderEmail}
                  onChange={(e) => {
                    setSenderEmail(e.target.value);
                    if (fieldErrors.senderEmail)
                      setFieldErrors((p) => ({ ...p, senderEmail: '' }));
                  }}
                  placeholder="e.g. agent@yourcompany.com"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-300 ${
                    fieldErrors.senderEmail ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                {fieldErrors.senderEmail && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.senderEmail}</p>
                )}
              </div>

              {/* Notification URL */}
              <div>
                <label
                  htmlFor={`notificationUrl-${agentType}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Notification URL
                  <span className="ml-1 text-xs font-normal text-gray-400">(MS Graph webhook endpoint)</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Public HTTPS URL that Microsoft Graph will POST to when new email arrives.
                  Required to create a webhook subscription. Leave empty to run without a subscription.
                </p>
                <input
                  id={`notificationUrl-${agentType}`}
                  type="url"
                  value={notificationUrl}
                  onChange={(e) => {
                    setNotificationUrl(e.target.value);
                    if (fieldErrors.notificationUrl)
                      setFieldErrors((p) => ({ ...p, notificationUrl: '' }));
                  }}
                  placeholder="e.g. https://ddq.cr.adalabsai.org/api/webhooks/graph"
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-300 ${
                    fieldErrors.notificationUrl ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                {fieldErrors.notificationUrl && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.notificationUrl}</p>
                )}
              </div>

              {/* Allowed Email Addresses */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allowed Email Addresses
                  <span className="ml-1 text-xs font-normal text-gray-400">(optional: emails or domains)</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  Leave empty to allow all senders. Add exact emails or domains like adalabsai.com.
                </p>

                {/* Chips */}
                {allowedEmails.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {allowedEmails.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full border border-blue-100"
                      >
                        {email}
                        <button
                          onClick={() => removeEmail(email)}
                          className="ml-0.5 text-blue-400 hover:text-blue-700 leading-none"
                          aria-label={`Remove ${email}`}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add row */}
                <div className="flex gap-2">
                  <input
                    ref={emailInputRef}
                    type="text"
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      if (emailInputError) setEmailInputError(null);
                    }}
                    onKeyDown={handleEmailInputKeyDown}
                    placeholder="Add email or domain and press Enter"
                    className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-300 ${
                      emailInputError ? 'border-red-400' : 'border-gray-300'
                    }`}
                  />
                  <button
                    onClick={addEmail}
                    className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add
                  </button>
                </div>

                {emailInputError && (
                  <p className="mt-1 text-xs text-red-500">{emailInputError}</p>
                )}
              </div>
            </div>

          ) : (
            /* ════════════════ STATUS & LOGS TAB ════════════════ */
            <div className="space-y-5">

              {/* Runtime status grid */}
              {detail && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Runtime Status
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">Status</p>
                      <p className="text-sm font-medium text-gray-800 capitalize">{detail.status}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">Running</p>
                      <p className="text-sm font-medium text-gray-800">
                        {detail.runtime.running ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">Subscription Active</p>
                      <p className="text-sm font-medium text-gray-800">
                        {detail.runtime.has_subscription ? 'Yes' : 'No'}
                      </p>
                    </div>
                    {detail.last_heartbeat && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-0.5">Last Heartbeat</p>
                        <p className="text-sm font-medium text-gray-800">
                          {new Date(detail.last_heartbeat).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recent activity */}
              {detail && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Recent Activity
                  </p>
                  {detail.recent_logs.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">No recent activity.</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-3 py-2 text-left text-gray-500 font-medium">Time</th>
                            <th className="px-3 py-2 text-left text-gray-500 font-medium">Event</th>
                            <th className="px-3 py-2 text-left text-gray-500 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.recent_logs.map((l) => (
                            <tr key={l.id} className="border-b border-gray-100 last:border-0">
                              <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                                {l.created_at ? new Date(l.created_at).toLocaleTimeString() : '—'}
                              </td>
                              <td className="px-3 py-2 text-gray-600">{l.event_type}</td>
                              <td className="px-3 py-2">
                                <span
                                  className={
                                    l.status === 'success'
                                      ? 'text-green-600 font-medium'
                                      : l.status === 'failed'
                                      ? 'text-red-600 font-medium'
                                      : 'text-yellow-600 font-medium'
                                  }
                                >
                                  {l.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          {error && !restartDialogOpen && (
            <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleRequestClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Cancel
            </button>
            {activeTab === 'configuration' && (
              <button
                onClick={handleSave}
                disabled={saving || loading || restartDialogOpen}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>

    <RestartAgentConfirmDialog
      open={restartDialogOpen}
      agentLabel={AGENT_LABELS[agentType] || agentType}
      onRestart={() => void handleRestartDialogRestart()}
      onDontRestart={() => void handleRestartDialogDontRestart()}
      onCancel={handleRestartDialogCancel}
      isBusy={saving}
      errorMessage={error}
    />
    </>
  );
}
